import {
    collection,
    doc,
    getDoc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    limit,
    startAfter,
    QueryDocumentSnapshot,
    DocumentData,
    Query,
    CollectionReference
} from 'firebase/firestore';
import { db } from '../../infrastructure/firebaseConfig';
import { logger } from '../../infrastructure/logger';

interface ErrorContext {
    operation: string;
    collectionName: string;
    id?: string;
    field?: string;
    additionalInfo?: Record<string, unknown>;
}

class RepositoryError extends Error {
    public readonly context: ErrorContext;
    public readonly originalError: unknown;

    constructor(message: string, context: ErrorContext, originalError: unknown) {
        super(message);
        this.name = 'RepositoryError';
        this.context = context;
        this.originalError = originalError;
        
        // Preserve stack trace from original error if available
        if (originalError instanceof Error && originalError.stack) {
            this.stack = `${this.stack}\nCaused by: ${originalError.stack}`;
        }
    }
}

export interface BaseEntity {
    id: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface PaginatedResult<T> {
    items: T[];
    lastDoc: QueryDocumentSnapshot<DocumentData> | null;
    hasMore: boolean;
}

export abstract class BaseRepository<T extends BaseEntity> {
    protected abstract collectionName: string;
    
    protected get collection(): CollectionReference<DocumentData> {
        return collection(db, this.collectionName);
    }
    
    protected abstract toEntity(doc: QueryDocumentSnapshot): T;
    
    protected toDocument(data: Partial<T>): DocumentData {
        const { id, createdAt, updatedAt, ...rest } = data as Record<string, unknown>;
        // Explicitly ignore these properties for destructuring
        void id;
        void createdAt; 
        void updatedAt;
        return rest as DocumentData;
    }
    
    async findById(id: string): Promise<T | undefined> {
        try {
            const docRef = doc(this.collection, id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() } as T;
            }
            return undefined;
        } catch (error) {
            logger.error(`Error fetching ${this.collectionName}/${id}`, error);
            throw new RepositoryError(
                `Failed to fetch ${this.collectionName}/${id}`,
                { operation: 'findById', collectionName: this.collectionName, id },
                error
            );
        }
    }
    
    async findAll(maxItems?: number): Promise<T[]> {
        try {
            let q: Query<DocumentData> = this.collection;
            if (maxItems) {
                q = query(q, limit(maxItems));
            }
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => this.toEntity(doc as QueryDocumentSnapshot<DocumentData>));
        } catch (error) {
            logger.error(`Error fetching all ${this.collectionName}`, error);
            throw new RepositoryError(
                `Failed to fetch all ${this.collectionName}`,
                { operation: 'findAll', collectionName: this.collectionName, additionalInfo: { maxItems } },
                error
            );
        }
    }
    
    async findByField(field: string, value: unknown): Promise<T[]> {
        try {
            const q = query(
                this.collection,
                where(field, '==', value)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => this.toEntity(doc as QueryDocumentSnapshot<DocumentData>));
        } catch (error) {
            logger.error(`Error fetching ${this.collectionName} by ${field}`, error);
            throw new RepositoryError(
                `Failed to fetch ${this.collectionName} by ${field}`,
                { operation: 'findByField', collectionName: this.collectionName, field, additionalInfo: { value } },
                error
            );
        }
    }
    
    async create(data: Omit<T, 'id'>): Promise<string> {
        try {
            const now = new Date().toISOString();
            const docData = {
                ...data,
                createdAt: now,
                updatedAt: now
            };
            const docRef = await addDoc(this.collection, this.toDocument(docData as T));
            return docRef.id;
        } catch (error) {
            logger.error(`Error creating ${this.collectionName}`, error);
            throw new RepositoryError(
                `Failed to create ${this.collectionName}`,
                { operation: 'create', collectionName: this.collectionName },
                error
            );
        }
    }
    
    async update(id: string, data: Partial<T>): Promise<void> {
        try {
            const docRef = doc(this.collection, id);
            const now = new Date().toISOString();
            await updateDoc(docRef, {
                ...this.toDocument(data),
                updatedAt: now
            });
        } catch (error) {
            logger.error(`Error updating ${this.collectionName}/${id}`, error);
            throw new RepositoryError(
                `Failed to update ${this.collectionName}/${id}`,
                { operation: 'update', collectionName: this.collectionName, id },
                error
            );
        }
    }
    
    async delete(id: string): Promise<void> {
        try {
            const docRef = doc(this.collection, id);
            await deleteDoc(docRef);
        } catch (error) {
            logger.error(`Error deleting ${this.collectionName}/${id}`, error);
            throw new RepositoryError(
                `Failed to delete ${this.collectionName}/${id}`,
                { operation: 'delete', collectionName: this.collectionName, id },
                error
            );
        }
    }
    
    async findWithPagination(
        pageSize: number,
        lastDoc?: QueryDocumentSnapshot<DocumentData> | null,
        orderByField: string = 'createdAt',
        direction: 'asc' | 'desc' = 'desc'
    ): Promise<PaginatedResult<T>> {
        try {
            let q: Query<DocumentData> = query(
                this.collection,
                orderBy(orderByField, direction),
                limit(pageSize + 1)
            );
            
            if (lastDoc) {
                q = query(q, startAfter(lastDoc));
            }
            
            const snapshot = await getDocs(q);
            const docs = snapshot.docs.slice(0, pageSize);
            const hasMore = snapshot.docs.length > pageSize;
            
            const items = docs.map(doc => this.toEntity(doc as QueryDocumentSnapshot<DocumentData>));
            
            return {
                items,
                lastDoc: docs[docs.length - 1] as QueryDocumentSnapshot<DocumentData> | null,
                hasMore
            };
        } catch (error) {
            logger.error(`Error fetching ${this.collectionName} with pagination`, error);
            throw new RepositoryError(
                `Failed to fetch ${this.collectionName} with pagination`,
                { operation: 'findWithPagination', collectionName: this.collectionName, additionalInfo: { pageSize, orderByField, direction } },
                error
            );
        }
    }
    
    async count(): Promise<number> {
        try {
            const snapshot = await getDocs(this.collection);
            return snapshot.size;
        } catch (error) {
            logger.error(`Error counting ${this.collectionName}`, error);
            throw new RepositoryError(
                `Failed to count ${this.collectionName}`,
                { operation: 'count', collectionName: this.collectionName },
                error
            );
        }
    }
    
    async exists(id: string): Promise<boolean> {
        try {
            const docRef = doc(this.collection, id);
            const docSnap = await getDoc(docRef);
            return docSnap.exists();
        } catch (error) {
            logger.error(`Error checking existence of ${this.collectionName}/${id}`, error);
            throw new RepositoryError(
                `Failed to check existence of ${this.collectionName}/${id}`,
                { operation: 'exists', collectionName: this.collectionName, id },
                error
            );
        }
    }
}

export { RepositoryError };
export type { ErrorContext };
